import { safeRedirect } from '@/lib/api/auth'
import { isApiError } from '@/lib/api/error'
import { redirect } from '@tanstack/react-router'
import type { AnyContext, ParsedLocation } from '@tanstack/react-router'
import { currentUserQuery } from '../queries/current-user-query'
import type { AuthStatus, User } from '../types'

interface BeforeLoadParams {
  context: { queryClient: import('@tanstack/react-query').QueryClient } & AnyContext
  location: ParsedLocation
}

/**
 * TanStack Router beforeLoad guard — ensures the user is authenticated.
 *
 * On success returns { user } merged into route context so descendant routes
 * can access `match.context.user` without re-fetching.
 *
 * On 401 (or unauthenticated status) throws a redirect to /login with
 * ?redirect=<current path> so the login page can bounce back after success.
 *
 * currentUserQuery resolves to AuthStatus | null.  We unwrap to User so
 * route context stays flat: `context.user` rather than `context.status.user`.
 */
export async function requireAuth({
  context,
  location,
}: BeforeLoadParams): Promise<{ user: User }> {
  try {
    // ensureQueryData return type is inferred as raw orval response because the
    // generated DataTag doesn't propagate the select-transformed TData shape.
    // Cast to the actual runtime shape (AuthStatus | null) from currentUserQuery's select.
    const status = (await context.queryClient.ensureQueryData(
      currentUserQuery(),
    )) as unknown as AuthStatus | null
    if (!status || !status.authenticated || !status.user) {
      throw redirect({
        to: '/login/',
        search: { redirect: safeRedirect(location.href) },
      })
    }
    return { user: status.user }
  } catch (error) {
    if (isApiError(error) && error.status === 401) {
      throw redirect({
        to: '/login/',
        search: { redirect: safeRedirect(location.href) },
      })
    }
    throw error
  }
}
